import { useParams } from '@tanstack/react-router'
import { ObjectChip } from 'features/objectViews'
import { useRolodexEntries } from 'features/rolodex'
import React from 'react'
import styled from 'styled-components'

interface SupplementaryLinksActivityContentProps {
  addedIds: string[]
  removedIds: string[]
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SectionTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const LoadingText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
`

export const SupplementaryLinksActivityContent: React.FC<
  SupplementaryLinksActivityContentProps
> = ({ addedIds, removedIds }) => {
  const allIds = [...addedIds, ...removedIds]

  const { siteID } = useParams({
    from: '/cases/$siteID/$caseID',
  })

  const { entries, isLoading } = useRolodexEntries(
    allIds.length > 0 ? { ids: allIds } : undefined
  )

  if (isLoading && allIds.length > 0) {
    return <LoadingText>Loading entries...</LoadingText>
  }

  const entriesMap = new Map(entries.map((entry) => [entry.id, entry]))

  return (
    <Container>
      {addedIds.length > 0 && (
        <Section>
          <SectionTitle>Added</SectionTitle>
          <ChipsContainer>
            {addedIds.map((id) => {
              const entry = entriesMap.get(id)
              if (!entry) return null

              return (
                <ObjectChip
                  key={id}
                  entry={entry}
                  siteID={siteID}
                  isEditMode={false}
                />
              )
            })}
          </ChipsContainer>
        </Section>
      )}

      {removedIds.length > 0 && (
        <Section>
          <SectionTitle>Removed</SectionTitle>
          <ChipsContainer>
            {removedIds.map((id) => {
              const entry = entriesMap.get(id)
              if (!entry) return null

              return (
                <ObjectChip
                  key={id}
                  entry={entry}
                  siteID={siteID}
                  isEditMode={false}
                />
              )
            })}
          </ChipsContainer>
        </Section>
      )}
    </Container>
  )
}
